import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, ArrowLeft, User, MapPin, Wrench, FileText, Calendar, Upload, X, File } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') + '/api';

const installationTypes = [
  { value: 'climatisation', label: 'Climatisation & Chauffage' },
  { value: 'ventilation', label: 'Ventilation' },
  { value: 'reparation', label: 'Réparation d\'électroménagers' },
  { value: 'construction_neuve', label: 'Construction neuve' },
  { value: 'autre', label: 'Autre' },
];

const days = [
  { id: 'lundi', label: 'Lun' },
  { id: 'mardi', label: 'Mar' },
  { id: 'mercredi', label: 'Mer' },
  { id: 'jeudi', label: 'Jeu' },
  { id: 'vendredi', label: 'Ven' },
  { id: 'samedi', label: 'Sam' }
];

const timeSlots = [
  { id: 'am', label: 'AM', hours: '8h - 12h' },
  { id: 'pm', label: 'PM', hours: '12h - 17h' }
];

// Saturday PM is not available
const isSlotAvailable = (dayId, slotId) => {
  if (dayId === 'samedi' && slotId === 'pm') return false;
  return true;
};

export const QuotePage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    installation_type: '',
    description: '',
    availability: {}
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, installation_type: value }));
    // Clear file if not construction neuve
    if (value !== 'construction_neuve') {
      setUploadedFile(null);
    }
  };

  const toggleAvailability = (day, time) => {
    if (!isSlotAvailable(day, time)) return;
    const key = `${day}_${time}`;
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [key]: !prev.availability[key]
      }
    }));
  };

  const getAvailabilityText = () => {
    const selected = Object.entries(formData.availability)
      .filter(([_, value]) => value)
      .map(([key]) => {
        const [day, time] = key.split('_');
        const dayLabel = days.find(d => d.id === day)?.label;
        return `${dayLabel} ${time.toUpperCase()}`;
      });
    return selected.length > 0 ? selected.join(', ') : 'Aucune disponibilité sélectionnée';
  };

  // File handling
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const validateFile = (file) => {
    const maxSize = 10 * 1024 * 1024; // 10 Mo
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    
    if (file.size > maxSize) {
      toast.error('Le fichier est trop volumineux (maximum 10 Mo)');
      return false;
    }
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez PDF, JPG ou PNG');
      return false;
    }
    
    return true;
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        setUploadedFile(file);
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        setUploadedFile(file);
      }
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateStep = (currentStep) => {
    if (currentStep === 1) {
      return formData.first_name && formData.last_name && formData.email && formData.phone;
    }
    if (currentStep === 2) {
      const hasAvailability = Object.values(formData.availability).some(v => v);
      return formData.address && hasAvailability;
    }
    if (currentStep === 3) {
      return formData.installation_type && formData.description;
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    } else {
      if (step === 2 && !Object.values(formData.availability).some(v => v)) {
        toast.error('Veuillez sélectionner au moins une disponibilité');
      } else {
        toast.error('Veuillez remplir tous les champs obligatoires');
      }
    }
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(3)) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      const submitData = new FormData();
      submitData.append('first_name', formData.first_name);
      submitData.append('last_name', formData.last_name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('address', formData.address);
      submitData.append('installation_type', formData.installation_type);
      submitData.append('description', formData.description);
      submitData.append('availability_text', getAvailabilityText());
      submitData.append('availability', JSON.stringify(formData.availability));
      
      if (uploadedFile) {
        submitData.append('file', uploadedFile);
      }

      await axios.post(`${API}/quotes`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setSubmitted(true);
      toast.success('Votre demande a été envoyée avec succès!');
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center py-20">
        <div className="max-w-md mx-auto text-center px-4">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-[#1a3a5c] mb-4">
            Demande envoyée!
          </h1>
          <p className="text-slate-600 mb-8">
            Merci pour votre demande de soumission. Notre équipe vous contactera 
            dans les plus brefs délais pour discuter de votre projet.
          </p>
          <Button 
            onClick={() => navigate('/')}
            className="rounded-full bg-[#00b4d8] hover:bg-[#009bb8] text-white px-8"
            data-testid="back-home-btn"
          >
            Retour à l'accueil
          </Button>
        </div>
      </div>
    );
  }

  const steps = [
    { number: 1, title: 'Contact', icon: User },
    { number: 2, title: 'Adresse', icon: MapPin },
    { number: 3, title: 'Projet', icon: Wrench },
    { number: 4, title: 'Confirmation', icon: FileText },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="hero-gradient py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-72 h-72 bg-[#00b4d8] rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Demande de soumission
          </h1>
          <p className="text-xl text-slate-300">
            Obtenez un devis gratuit pour votre projet
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Progress steps */}
          <div className="flex justify-between mb-12 relative">
            <div className="absolute top-5 left-0 right-0 h-0.5 bg-slate-200 -z-10"></div>
            <div 
              className="absolute top-5 left-0 h-0.5 bg-[#00b4d8] -z-10 transition-all duration-500"
              style={{ width: `${((step - 1) / 3) * 100}%` }}
            ></div>
            {steps.map((s) => {
              const Icon = s.icon;
              const isActive = step >= s.number;
              const isCurrent = step === s.number;
              return (
                <div key={s.number} className="flex flex-col items-center">
                  <div 
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                      isActive 
                        ? 'bg-[#00b4d8] text-white' 
                        : 'bg-slate-200 text-slate-400'
                    } ${isCurrent ? 'ring-4 ring-[#00b4d8]/20' : ''}`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className={`mt-2 text-sm font-medium ${isActive ? 'text-[#1a3a5c]' : 'text-slate-400'}`}>
                    {s.title}
                  </span>
                </div>
              );
            })}
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-8 border border-slate-100">
            {/* Step 1: Contact Info */}
            {step === 1 && (
              <div className="space-y-6 animate-fade-in-up" data-testid="step-1">
                <h2 className="text-2xl font-bold text-[#1a3a5c] mb-6">
                  Vos informations de contact
                </h2>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="first_name">Prénom *</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      placeholder="Votre prénom"
                      className="mt-2 input-styled"
                      data-testid="first-name-input"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Nom *</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      placeholder="Votre nom"
                      className="mt-2 input-styled"
                      data-testid="last-name-input"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email">Courriel *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="votre@courriel.com"
                    className="mt-2 input-styled"
                    data-testid="email-input"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Téléphone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(450) 555-1234"
                    className="mt-2 input-styled"
                    data-testid="phone-input"
                    required
                  />
                </div>
              </div>
            )}

            {/* Step 2: Address & Availability */}
            {step === 2 && (
              <div className="space-y-6 animate-fade-in-up" data-testid="step-2">
                <h2 className="text-2xl font-bold text-[#1a3a5c] mb-6">
                  Adresse et disponibilités
                </h2>
                <div>
                  <Label htmlFor="address">Adresse complète *</Label>
                  <Textarea
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Numéro, rue, ville, code postal"
                    className="mt-2 input-styled min-h-[100px]"
                    data-testid="address-input"
                    required
                  />
                </div>
                
                {/* Availability Grid */}
                <div className="space-y-4 pt-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-[#00b4d8]" />
                    <Label>Vos disponibilités *</Label>
                  </div>
                  <p className="text-sm text-slate-500">Cochez les plages horaires où vous êtes disponible</p>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr>
                          <th className="p-2 text-left text-sm font-medium text-slate-500"></th>
                          {days.map((day) => (
                            <th key={day.id} className="p-2 text-center text-sm font-semibold text-[#1a3a5c]">
                              {day.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {timeSlots.map((slot) => (
                          <tr key={slot.id}>
                            <td className="p-2 text-sm font-medium text-slate-600">
                              <div>{slot.label}</div>
                              <div className="text-xs text-slate-400">{slot.hours}</div>
                            </td>
                            {days.map((day) => {
                              const isAvailable = isSlotAvailable(day.id, slot.id);
                              const isSelected = formData.availability[`${day.id}_${slot.id}`];
                              return (
                                <td key={`${day.id}_${slot.id}`} className="p-2 text-center">
                                  {isAvailable ? (
                                    <button
                                      type="button"
                                      onClick={() => toggleAvailability(day.id, slot.id)}
                                      className={`w-10 h-10 rounded-lg border-2 transition-all ${
                                        isSelected
                                          ? 'bg-[#00b4d8] border-[#00b4d8] text-white'
                                          : 'border-slate-200 hover:border-[#00b4d8] text-slate-400'
                                      }`}
                                    >
                                      {isSelected && (
                                        <svg className="w-5 h-5 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                      )}
                                    </button>
                                  ) : (
                                    <div className="w-10 h-10 rounded-lg bg-slate-100 border-2 border-slate-100 flex items-center justify-center">
                                      <X className="w-4 h-4 text-slate-300" />
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-slate-400 italic">* Samedi après-midi et dimanche non disponibles</p>
                </div>
              </div>
            )}

            {/* Step 3: Project */}
            {step === 3 && (
              <div className="space-y-6 animate-fade-in-up" data-testid="step-3">
                <h2 className="text-2xl font-bold text-[#1a3a5c] mb-6">
                  Détails du projet
                </h2>
                <div>
                  <Label>Type d'installation *</Label>
                  <Select 
                    value={formData.installation_type} 
                    onValueChange={handleSelectChange}
                  >
                    <SelectTrigger className="mt-2 input-styled" data-testid="type-select">
                      <SelectValue placeholder="Sélectionnez un type" />
                    </SelectTrigger>
                    <SelectContent>
                      {installationTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* File upload for Construction neuve */}
                {formData.installation_type === 'construction_neuve' && (
                  <div className="space-y-2">
                    <Label>Plan du projet (optionnel)</Label>
                    <p className="text-sm text-slate-500">PDF, JPG ou PNG - Maximum 10 Mo</p>
                    
                    {!uploadedFile ? (
                      <div
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                          dragActive
                            ? 'border-[#00b4d8] bg-[#00b4d8]/5'
                            : 'border-slate-300 hover:border-slate-400'
                        }`}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleFileChange}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        <Upload className="w-10 h-10 mx-auto text-slate-400 mb-4" />
                        <p className="text-slate-600 font-medium">
                          Glissez-déposez votre fichier ici
                        </p>
                        <p className="text-slate-400 text-sm mt-1">
                          ou cliquez pour sélectionner
                        </p>
                      </div>
                    ) : (
                      <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                        <div className="w-12 h-12 rounded-lg bg-[#00b4d8]/10 flex items-center justify-center">
                          <File className="w-6 h-6 text-[#00b4d8]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#1a3a5c] truncate">{uploadedFile.name}</p>
                          <p className="text-sm text-slate-500">
                            {(uploadedFile.size / 1024 / 1024).toFixed(2)} Mo
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={removeFile}
                          className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                        >
                          <X className="w-5 h-5 text-slate-500" />
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <Label htmlFor="description">Description du projet *</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Décrivez votre projet en détail: type de travaux, particularités, contraintes..."
                    className="mt-2 input-styled min-h-[150px]"
                    data-testid="description-input"
                    required
                  />
                </div>
              </div>
            )}

            {/* Step 4: Confirmation */}
            {step === 4 && (
              <div className="space-y-6 animate-fade-in-up" data-testid="step-4">
                <h2 className="text-2xl font-bold text-[#1a3a5c] mb-6">
                  Confirmez votre demande
                </h2>
                <div className="bg-slate-50 rounded-xl p-6 space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Nom</p>
                      <p className="font-medium text-[#1a3a5c]">{formData.first_name} {formData.last_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Téléphone</p>
                      <p className="font-medium text-[#1a3a5c]">{formData.phone}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Courriel</p>
                      <p className="font-medium text-[#1a3a5c]">{formData.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Type</p>
                      <p className="font-medium text-[#1a3a5c]">
                        {installationTypes.find(t => t.value === formData.installation_type)?.label}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Adresse</p>
                    <p className="font-medium text-[#1a3a5c]">{formData.address}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Disponibilités</p>
                    <p className="font-medium text-[#1a3a5c]">{getAvailabilityText()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Description</p>
                    <p className="font-medium text-[#1a3a5c]">{formData.description}</p>
                  </div>
                  {uploadedFile && (
                    <div>
                      <p className="text-sm text-slate-500">Fichier joint</p>
                      <p className="font-medium text-[#1a3a5c]">{uploadedFile.name}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {step === 4 && (
              <p className="text-sm text-slate-500">
              En transmettant ce formulaire, vous consentez à l’utilisation de vos renseignements pour traiter votre demande. Consultez notre <a href="/politique-confidentialite" className="text-[#0077b6] underline">politique de confidentialité</a>.
            </p>
            )}

            {/* Navigation buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-slate-100">
              {step > 1 ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={prevStep}
                  className="rounded-full border-slate-300"
                  data-testid="prev-btn"
                >
                  <ArrowLeft className="mr-2 w-4 h-4" />
                  Précédent
                </Button>
              ) : (
                <div></div>
              )}

              {step < 4 ? (
                <Button
                  type="button"
                  onClick={nextStep}
                  className="rounded-full bg-[#00b4d8] hover:bg-[#009bb8] text-white px-8"
                  data-testid="next-btn"
                >
                  Suivant
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={loading}
                  className="rounded-full bg-[#00b4d8] hover:bg-[#009bb8] text-white px-8"
                  data-testid="submit-btn"
                >
                  {loading ? 'Envoi en cours...' : 'Envoyer ma demande'}
                </Button>
              )}
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default QuotePage;
